/* eslint-disable */
import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft, Check, BookOpen, Users, Clock, Play, 
  TrendingUp, Layers, Award, Tag, Star, Brain,
  Flame, BookMarked, Target, ChevronDown, ChevronUp,
  Trophy, AlertTriangle, BarChart2, Activity,
  CheckCircle, Circle, Minus, Highlighter, StickyNote,
} from 'lucide-react';
import Page from '../../components/Page';
import adminServices from '../../services/adminServices';

/* ─── mini bar ───────────────────────────────────────────────────── */
function Bar({ value, max, color = '#6366f1', height = 6 }) {
  const w = Math.min((value / Math.max(max, 1)) * 100, 100);
  return (
    <div style={{ width: '100%', background: '#f1f5f9', borderRadius: 9999, height }}>
      <div style={{ height, borderRadius: 9999, transition: 'width 0.4s ease', width: `${w}%`, background: color }} />
    </div>
  );
}

/* ─── skeleton ───────────────────────────────────────────────────── */
const pulse = 'animate-pulse bg-slate-200 rounded-xl';
function Sk({ className = '' }) { return <div className={`${pulse} ${className}`} />; }

function PageSkeleton() {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200 px-4 sm:px-8 py-8">
        <Sk className="w-16 h-4 mb-6" />
        <div className="flex flex-col lg:flex-row gap-8">
          <Sk className="w-40 h-56 flex-shrink-0" />
          <div className="flex-1 flex flex-col gap-3">
            <div className="flex gap-2"><Sk className="w-20 h-6" /><Sk className="w-16 h-6" /></div>
            <Sk className="w-3/4 h-8" />
            <Sk className="w-full h-4" />
            <Sk className="w-5/6 h-4" />
            <Sk className="w-48 h-3 mt-2" />
          </div>
          <Sk className="w-full lg:w-56 h-44 flex-shrink-0" />
        </div>
      </div>
      <div className="px-4 sm:px-8 py-8 flex flex-col gap-4">
        <div className="flex gap-4"><Sk className="w-24 h-4" /><Sk className="w-24 h-4" /><Sk className="w-24 h-4" /></div>
        <div className="grid grid-cols-3 gap-3">{[1,2,3].map(i=><Sk key={i} className="h-20"/>)}</div>
        <Sk className="h-48" /><Sk className="h-36" />
      </div>
    </div>
  );
}

function AnalyticsSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">{[1,2,3,4].map(i=><Sk key={i} className="h-24"/>)}</div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4"><Sk className="h-40"/><Sk className="h-40"/></div>
      <Sk className="h-40"/>
    </div>
  );
}

/* ─── CTAPanel — price / progress info + the primary action button ─ */
function CTAPanel({ plan, progress, cta, error }) {
  const isEnrolled = plan && ['ACTIVE','CANCELED','EXPIRED'].includes(plan.enrollmentStatus);
  const isFree     = plan?.isFree;
  const sym        = plan?.currencyCode === 'USD' ? '$' : '₹';

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 w-full lg:w-64 flex-shrink-0 flex flex-col gap-4 self-start lg:self-stretch">
      {!plan ? (
        <p className="text-xs text-slate-400 text-center">Read this book anytime</p>
      ) : isFree ? (
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold text-slate-500">Access</span>
          <span className="text-sm font-black px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Free</span>
        </div>
      ) : isEnrolled ? (
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold text-slate-500">Plan</span>
          <span className="text-sm font-black px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">{plan.name}</span>
        </div>
      ) : (
        <div>
          <p className="text-xs text-slate-400 mb-1">One-time / Subscription</p>
          <div className="flex items-baseline gap-2">
            {plan.discount > 0 && <span className="text-sm text-slate-400 line-through">{sym}{plan.actual_cost}</span>}
            <span className="text-2xl font-black text-slate-900">{sym}{plan.payable_cost}</span>
            {plan.discount > 0 && (
              <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">{plan.discount}% off</span>
            )}
          </div>
        </div>
      )}

      {(isEnrolled || progress?.hasStarted) && progress && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>{progress.topicsCompleted}/{progress.topicsTotal} topics</span>
            <span>{progress.progressPct}%</span>
          </div>
          <Bar value={progress.topicsCompleted} max={progress.topicsTotal} />
          {progress.totalTimeSpentMinutes > 0 && (
            <p className="text-xs text-slate-400 flex items-center gap-1">
              <Clock size={11}/> {progress.totalTimeSpentMinutes} mins spent
            </p>
          )}
        </div>
      )}

      {isEnrolled && plan.enrolledAt && (
        <div className="flex flex-col gap-1 text-xs text-slate-400 border-t border-slate-100 pt-3">
          <span>Enrolled: {new Date(plan.enrolledAt).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}</span>
          {plan.expiryDate && <span>Expires: {new Date(plan.expiryDate).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}</span>}
        </div>
      )}

      {cta && (
        <button type="button" onClick={cta.onClick}
          className={`w-full mt-auto px-4 py-3 rounded-xl text-white font-bold text-sm flex items-center justify-center gap-2 transition-colors ${cta.cls}`}>
          {React.createElement(cta.icon, { size: 15 })} {cta.label}
        </button>
      )}
      {error && <p className="text-xs text-rose-600 font-semibold text-center">{error}</p>}
    </div>
  );
}

/* ─── topic status dot ───────────────────────────────────────────── */
function TopicStatus({ status }) {
  if (status === 'completed')   return <CheckCircle size={13} className="text-emerald-500 flex-shrink-0" />;
  if (status === 'in_progress') return <Circle size={13} className="text-indigo-500 flex-shrink-0" />;
  return <Minus size={13} className="text-slate-300 flex-shrink-0" />;
}

/* ─── stat card ──────────────────────────────────────────────────── */
function StatCard({ label, value, sub, icon: Icon, iconColor = '#6366f1', iconBg = '#eef2ff' }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-500">{label}</span>
        <div style={{ background: iconBg, borderRadius: 10, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={15} style={{ color: iconColor }} />
        </div>
      </div>
      <div>
        <p className="text-2xl font-black text-slate-900 leading-none">{value}</p>
        {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
      </div>
    </div>
  );
}

/* ─── predict bar ────────────────────────────────────────────────── */
function PredictBar({ label, value, color }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-slate-500 w-28 flex-shrink-0">{label}</span>
      <div style={{ flex: 1, background: '#f1f5f9', borderRadius: 9999, height: 5 }}>
        <div style={{ height: 5, borderRadius: 9999, width: `${value}%`, background: color, transition: 'width 0.5s ease' }} />
      </div>
      <span className="text-xs font-semibold text-slate-600 w-8 text-right">{Math.round(value)}%</span>
    </div>
  );
}

/* ─── nudge card ─────────────────────────────────────────────────── */
function Nudge({ icon: Icon, iconColor, iconBg, borderColor, bg, title, desc }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 14px', borderRadius: 12, border: `1px solid ${borderColor}`, background: bg }}>
      <div style={{ width: 30, height: 30, borderRadius: 8, background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={15} style={{ color: iconColor }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', margin: '0 0 2px' }}>{title}</p>
        <p style={{ fontSize: 12, color: '#64748b', margin: 0, lineHeight: 1.5 }}>{desc}</p>
      </div>
    </div>
  );
}

/* ─── badge chip ─────────────────────────────────────────────────── */
function Badge({ icon: Icon, label, earned }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5,
      padding: '10px 14px', borderRadius: 12,
      border: earned ? '1px solid #c7d2fe' : '1px solid #e2e8f0',
      background: earned ? '#eef2ff' : '#f8fafc',
      opacity: earned ? 1 : 0.45, minWidth: 76,
    }}>
      <Icon size={20} style={{ color: earned ? '#6366f1' : '#94a3b8' }} />
      <span style={{ fontSize: 11, fontWeight: earned ? 700 : 400, color: earned ? '#4338ca' : '#94a3b8', textAlign: 'center', lineHeight: 1.3 }}>{label}</span>
    </div>
  );
}

/* ─── score trend dots ───────────────────────────────────────────── */
function ScoreTrend({ scores = [] }) {
  if (!scores.length) return <p className="text-xs text-slate-400 py-4 text-center">No assessment data yet</p>;
  const max = Math.max(...scores, 100);
  const H = 64, W = 100;
  const pts = scores.map((v, i) => {
    const x = scores.length === 1 ? W / 2 : (i / (scores.length - 1)) * W;
    const y = H - (v / max) * H;
    return { x, y, v };
  });
  const last = pts[pts.length - 1];
  const prev = pts[pts.length - 2];
  const trend = prev ? (last.v > prev.v ? 'up' : last.v < prev.v ? 'down' : 'flat') : 'flat';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} style={{ flex: 1 }}>
        <polyline points={pts.map(p => `${p.x},${p.y}`).join(' ')}
          fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        {pts.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={3} fill="#6366f1"/>
        ))}
      </svg>
      <div style={{ flexShrink: 0, textAlign: 'right' }}>
        <p style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', margin: 0 }}>{last.v}%</p>
        <p style={{ fontSize: 11, color: trend === 'up' ? '#16a34a' : trend === 'down' ? '#dc2626' : '#94a3b8', margin: 0 }}>
          {trend === 'up' ? '↑ improving' : trend === 'down' ? '↓ dropped' : '→ steady'}
        </p>
      </div>
    </div>
  );
}

/* ─── chapter row ────────────────────────────────────────────────── */
function ChapterProgressRow({ ch, index }) {
  const done  = ch.topics?.filter(t => t.progressStatus === 'completed').length || 0;
  const total = ch.topics?.length || 0;
  const pct   = total > 0 ? Math.round((done / total) * 100) : 0;
  const color = pct === 100 ? '#10b981' : pct > 0 ? '#6366f1' : '#cbd5e1';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ width: 24, height: 24, borderRadius: 6, background: pct === 100 ? '#d1fae5' : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: pct === 100 ? '#059669' : '#94a3b8' }}>{index + 1}</span>
      </div>
      <span style={{ fontSize: 13, color: '#334155', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ch.title}</span>
      <div style={{ width: 120, flexShrink: 0 }}>
        <Bar value={done} max={total} color={color} height={5} />
      </div>
      <span style={{ fontSize: 12, color: '#94a3b8', width: 36, textAlign: 'right', flexShrink: 0 }}>{done}/{total}</span>
    </div>
  );
}

const TABS = ['Overview', 'Curriculum', 'Analytics'];

/* ══════════════════════════════════════════════════════════════════
   MAIN
══════════════════════════════════════════════════════════════════ */
export default function EBookDetails() {
  const { ebookId } = useParams();
  const navigate    = useNavigate();
  const location    = useLocation();
  // These screens are mounted under /app for AUTH_USER and /auth for USER_DTC,
  // so every in-app link has to be built off whichever prefix we're actually on.
  const areaBase    = location.pathname.startsWith('/auth') ? '/auth' : '/app';
  const [tab, setTab]         = useState(0);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDetail, setShowDetail] = useState(false);
  const [openCh, setOpenCh] = useState({});

  const [analyticsData, setAnalyticsData]       = useState(null);
  console.log(analyticsData,'analyticsDataanalyticsDataanalyticsData')
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError]     = useState(false);

  const [enrolling, setEnrolling] = useState(false);
  const [enrolError, setEnrolError] = useState('');

  const loadDetails = React.useCallback(async ({ silent } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await adminServices.getEbookFullDetails(ebookId);
      setData(res?.data ?? null);
    } catch (e) { console.error(e); }
    finally { if (!silent) setLoading(false); }
  }, [ebookId]);

  React.useEffect(() => { loadDetails(); }, [loadDetails]);

  const handleTabClick = async (i) => {
    setTab(i);
    if (i === 2) {
      setAnalyticsLoading(true);
      setAnalyticsError(false);
      try {
        const res = await adminServices.getEbookFullAnalytics(ebookId);
        setAnalyticsData(res?.data ?? null);
      } catch (e) {
        console.error(e);
        setAnalyticsError(true);
      } finally {
        setAnalyticsLoading(false);
      }
    }
  };

  if (loading) return <Page title="eBook"><PageSkeleton /></Page>;
  if (!data) return (
    <Page title="eBook">
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-slate-500 font-semibold">Ebook not found.</p>
      </div>
    </Page>
  );

  const { ebook, plan, chapters, progress } = data;

  const totalTopics    = chapters.reduce((s, c) => s + (c.topics?.length || 0), 0);
  const estReadMinutes = chapters.reduce(
    (s, c) => s + (c.topics || []).reduce((t, tp) => t + (tp.readTimeMinutes || 0), 0), 0,
  );
  const isEnrolled     = plan && ['ACTIVE','CANCELED','EXPIRED'].includes(plan.enrollmentStatus);
  const hasStartedMain = progress?.hasStarted;
  const relatedContent = ebook.related_content || {};
  const prereqEbooks      = relatedContent.ebooks      || [];
  const prereqCourses     = relatedContent.courses     || [];
  const prereqAssessments = relatedContent.assessments || [];
  const hasPrereqs = prereqEbooks.length + prereqCourses.length + prereqAssessments.length > 0;

  // Resolved from the API: series (assessment type) name + the assessment
  // chapters this book is tagged to teach.
  const seriesName     = ebook.seriesName || null;
  const coveredChapters = Array.isArray(ebook.relatedChapters) ? ebook.relatedChapters : [];
  const sym            = plan?.currencyCode === 'USD' ? '$' : '₹';

  // Details grid shown on the Overview tab — every field we have.
  const bookFacts = [
    seriesName        && { label: 'Series',        value: seriesName },
    ebook.subject_id  && { label: 'Subject',       value: ebook.subject_id },
    ebook.class_level && { label: 'Class Level',   value: ebook.class_level },
    ebook.board       && { label: 'Board',         value: ebook.board },
    ebook.language    && { label: 'Language',      value: ebook.language },
    { label: 'Chapters', value: chapters.length },
    { label: 'Topics',   value: totalTopics },
    estReadMinutes > 0 && { label: 'Est. Reading', value: `${Math.round(estReadMinutes)} min` },
  ].filter(Boolean);

  // The reader route is `<area>/e-book/:ebookId` (see routes.js) — the old
  // `/app/user/e-book/:id/read` path never existed, so Continue 404'd.
  const handleContinue  = () => navigate(`${areaBase}/e-book/${ebookId}`);
  // Jump the reader straight to one topic (Curriculum tab "Read Topic" buttons).
  const handleReadTopic = (topicId) => navigate(`${areaBase}/e-book/${ebookId}?topicId=${topicId}`);
  const toggleCh = (id, ci) => setOpenCh((p) => ({ ...p, [id]: !(p[id] ?? ci === 0) }));

  // Free plan → enrol via the API, then refresh the details so the CTA flips to
  // "Start Reading". Paid plans have no checkout route yet, so those still go to
  // the catalogue.
  const handleEnrol = async () => {
    if (enrolling) return;
    setEnrolling(true);
    setEnrolError('');
    try {
      const res = await adminServices.enrollFreeEbook(ebookId, plan?.id ?? plan?.planId);
      if (res?.ok) {
        await loadDetails({ silent: true });
      } else {
        setEnrolError(res?.data?.message || 'Could not enrol. Please try again.');
      }
    } catch (e) {
      console.error(e);
      setEnrolError('Could not enrol. Please try again.');
    } finally {
      setEnrolling(false);
    }
  };
  const handleSubscribe = () => navigate(`${areaBase}/e-books`);

  // The primary action, shown at the bottom-right of the hero (inside CTAPanel).
  // Once the learner is enrolled OR has opened the book at all, it is always
  // "Continue / Start Reading" — never "Enrol Now" again.
  const canRead = isEnrolled || hasStartedMain;
  let headerCta;
  if (canRead) {
    headerCta = { label: hasStartedMain ? 'Continue Reading' : 'Start Reading', icon: Play, onClick: handleContinue, cls: 'bg-indigo-600 hover:bg-indigo-700' };
  } else if (plan && plan.isFree) {
    headerCta = { label: enrolling ? 'Enrolling…' : 'Enrol Now — Free', icon: Check, onClick: handleEnrol, cls: `bg-emerald-600 hover:bg-emerald-700 ${enrolling ? 'opacity-70 pointer-events-none' : ''}` };
  } else if (plan) {
    headerCta = { label: `Enrol Now · ${sym}${plan.payable_cost ?? plan.payableCost ?? ''}`, icon: Tag, onClick: handleSubscribe, cls: 'bg-indigo-600 hover:bg-indigo-700' };
  } else {
    headerCta = { label: 'Read E-Book', icon: Play, onClick: handleContinue, cls: 'bg-indigo-600 hover:bg-indigo-700' };
  }

  /* ── analytics derived values (from analytics API) ───────────── */
  const ov   = analyticsData?.overview   || {};
  const pred = analyticsData?.prediction || {};
  const eng  = analyticsData?.engagement || {};
  const rb   = analyticsData?.readingBehaviour || {};
  const anChapters = analyticsData?.chapters || [];

  const totalMin       = ov.totalTimeSpentMinutes || 0;
  const avgScore       = Math.round(ov.avgScore || 0);
  const streakDays     = ov.streakDays || 0;
  const bestStreak     = ov.bestStreak || streakDays;
  const completionPct  = ov.progressPct || 0;
  const topicsDone     = ov.topicsCompleted || 0;
  const topicsTotal    = ov.topicsTotal || totalTopics;
  const chapsDone      = ov.chaptersCompleted || 0;
  const chapsTotal     = ov.chaptersTotal || chapters.length;

  const predicted    = Math.round(pred.predictedScore || 0);
  const consistency  = pred.consistency || 0;
  const scoreHistory = pred.scoreHistory || [];

  const highlights = eng.highlightsCount || 0;
  const notes      = eng.notesCount || 0;
  const bookmarks  = eng.bookmarksCount || 0;

  const avgTimePerTopic     = rb.avgTimePerTopicMinutes || 0;
  const topicsReadToday     = rb.topicsReadToday || 0;
  const bestStudyDay         = rb.bestStudyDay || '—';
  const estimatedMinutesLeft = rb.estimatedMinutesLeft;
  const topicsRemaining      = rb.topicsRemaining ?? (topicsTotal - topicsDone);

  const hasStarted = analyticsData?.hasStarted;

  /* ── nudges: build dynamically based on real data ────────────── */
  const nudges = [];
  if (streakDays > 0 && streakDays < 7) nudges.push({
    icon: Flame, iconColor: '#b45309', iconBg: '#fde68a', borderColor: '#fcd34d44', bg: '#fffbeb',
    title: `Keep your ${streakDays}-day streak going`,
    desc: 'Read at least one topic today to maintain your streak',
  });
  if (avgScore > 0 && avgScore < 60) nudges.push({
    icon: AlertTriangle, iconColor: '#991b1b', iconBg: '#fee2e2', borderColor: '#fca5a544', bg: '#fef2f2',
    title: 'Assessment scores need attention',
    desc: 'Your average is below 60% — try using Ask AI to revisit difficult topics',
  });
  if (bookmarks > 0) nudges.push({
    icon: BookMarked, iconColor: '#1e40af', iconBg: '#dbeafe', borderColor: '#93c5fd44', bg: '#eff6ff',
    title: `${bookmarks} bookmarked topic${bookmarks > 1 ? 's' : ''} waiting`,
    desc: 'You saved these earlier — pick up where you left off',
  });
  if (completionPct === 100) nudges.push({
    icon: Trophy, iconColor: '#15803d', iconBg: '#dcfce7', borderColor: '#86efac44', bg: '#f0fdf4',
    title: 'Book fully completed!',
    desc: 'You have finished every topic in this book — great work',
  });
  if (nudges.length === 0 && topicsDone > 0) nudges.push({
    icon: Target, iconColor: '#7c3aed', iconBg: '#ede9fe', borderColor: '#c4b5fd44', bg: '#faf5ff',
    title: `${topicsRemaining} topic${topicsRemaining !== 1 ? 's' : ''} remaining`,
    desc: `You are ${completionPct}% through — keep going to complete this book`,
  });

  /* ── badges ──────────────────────────────────────────────────── */
  const badges = [
    { icon: BookOpen,   label: 'First read',     earned: topicsDone >= 1 },
    { icon: Flame,      label: '5-day streak',   earned: bestStreak >= 5 },
    { icon: CheckCircle,label: 'Chapter done',   earned: chapsDone >= 1 },
    { icon: Trophy,     label: 'Top scorer',     earned: avgScore >= 85 },
    { icon: Target,     label: '100% complete',  earned: completionPct === 100 },
    { icon: Brain,      label: 'Deep reader',    earned: highlights >= 10 },
  ];

  return (
    <Page title={ebook.title || 'eBook'}>
      <div className="min-h-screen bg-slate-50">

        {/* ── HERO ─────────────────────────────────────────────── */}
        <div className="bg-white border-b border-slate-200">
          <div className=" mx-auto px-4 sm:px-8 py-8">
            <button type="button" onClick={() => navigate(-1)}
              className="flex items-center gap-1.5 text-slate-500 hover:text-slate-800 text-sm font-semibold mb-6 transition-colors">
              <ArrowLeft size={14}/> Back
            </button>

            <div className="flex flex-col lg:flex-row gap-8">
              <div className="w-40 h-56 rounded-2xl overflow-hidden border border-slate-200 flex-shrink-0 self-start bg-slate-100">
                {ebook.cover_image_url ? (
                  <img src={ebook.cover_image_url} alt={ebook.title} className="w-full h-full object-cover"/>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                    <BookOpen size={40} className="text-slate-300"/>
                    <span className="text-xs text-slate-400 font-medium">No Cover</span>
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap gap-2 mb-3">
                  {seriesName        && <span className="text-xs font-bold px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-100">{seriesName}</span>}
                  {ebook.subject_id  && <span className="text-xs font-bold px-2.5 py-1 rounded-lg bg-blue-50 text-blue-700 border border-blue-100">{ebook.subject_id}</span>}
                  {ebook.class_level && <span className="text-xs font-bold px-2.5 py-1 rounded-lg bg-slate-100 text-slate-600 border border-slate-200">{ebook.class_level}</span>}
                  {ebook.board       && <span className="text-xs font-bold px-2.5 py-1 rounded-lg bg-slate-100 text-slate-600 border border-slate-200">{ebook.board}</span>}
                  {ebook.language    && <span className="text-xs font-bold px-2.5 py-1 rounded-lg bg-slate-100 text-slate-600 border border-slate-200">{ebook.language}</span>}
                  {plan?.isFree      && <span className="text-xs font-bold px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200">Free</span>}
                </div>
                <h1 className="text-2xl sm:text-3xl font-black text-slate-900 leading-tight mb-2">{ebook.title}</h1>
                {ebook.author && <p className="text-sm text-slate-500 font-medium mb-2">by {ebook.author}</p>}
                <p className="text-sm text-slate-500 leading-relaxed mb-4 max-w-2xl">{ebook.short_description || ebook.description || 'No description available.'}</p>
                <div className="flex flex-wrap gap-5 text-sm text-slate-500 mb-5">
                  <span className="flex items-center gap-1.5"><Layers size={14} className="text-slate-400"/> {chapters.length} Chapters</span>
                  <span className="flex items-center gap-1.5"><BookOpen size={14} className="text-slate-400"/> {totalTopics} Topics</span>
                  {estReadMinutes > 0 && <span className="flex items-center gap-1.5"><Clock size={14} className="text-slate-400"/> {Math.round(estReadMinutes)} min read</span>}
                  {ebook.learner_count > 0 && <span className="flex items-center gap-1.5"><Users size={14} className="text-slate-400"/> {ebook.learner_count.toLocaleString()} Learners</span>}
                </div>
                {(isEnrolled || hasStartedMain) && progress && (
                  <div className="max-w-sm">
                    <div className="flex justify-between text-xs text-slate-500 font-semibold mb-1.5">
                      <span>Your Progress</span><span>{progress.progressPct}%</span>
                    </div>
                    <Bar value={progress.topicsCompleted} max={progress.topicsTotal} />
                    <p className="text-xs text-slate-400 mt-1.5">{progress.topicsCompleted} of {progress.topicsTotal} topics completed</p>
                  </div>
                )}
              </div>
              <CTAPanel plan={plan} progress={progress} cta={headerCta} error={enrolError}/>
            </div>
          </div>
        </div>

        {/* ── TABS ─────────────────────────────────────────────── */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200">
          <div className=" mx-auto px-4 sm:px-8">
            <div className="flex overflow-x-auto">
              {TABS.map((t, i) => (
                <button key={t} type="button" onClick={() => handleTabClick(i)}
                  className={`px-5 py-3.5 text-sm font-bold border-b-2 transition-colors whitespace-nowrap
                    ${tab === i ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── BODY ─────────────────────────────────────────────── */}
        <div className=" mx-auto px-4 sm:px-8 py-8">

          {/* ══ OVERVIEW ═════════════════════════════════════════ */}
          {tab === 0 && (
            <div className="flex flex-col gap-6">

              {/* progress stat cards (only once the learner has started) */}
              {(progress?.topicsCompleted > 0 || progress?.totalTimeSpentMinutes > 0) && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: 'Chapters',  value: chapters.length,  icon: Layers },
                    { label: 'Topics',    value: totalTopics,       icon: BookOpen },
                    { label: 'Completed', value: `${progress.topicsCompleted}/${progress.topicsTotal}`, icon: Check },
                    progress?.totalTimeSpentMinutes > 0 ? { label: 'Time Spent', value: `${progress.totalTimeSpentMinutes}m`, icon: Clock } : null,
                  ].filter(Boolean).map(({ label, value, icon }) => (
                    <div key={label} className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
                        {React.createElement(icon, { size: 16, className: 'text-indigo-600' })}
                      </div>
                      <div>
                        <p className="text-lg font-black text-slate-900">{value}</p>
                        <p className="text-xs text-slate-500 font-medium">{label}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Book details — every field we have */}
              <div className="bg-white border border-slate-200 rounded-2xl p-6">
                <h2 className="text-base font-black text-slate-900 mb-4">Book details</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-4">
                  {bookFacts.map((f) => (
                    <div key={f.label}>
                      <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">{f.label}</p>
                      <p className="text-sm font-semibold text-slate-800">{f.value}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* About */}
              <div className="bg-white border border-slate-200 rounded-2xl p-6">
                <h2 className="text-base font-black text-slate-900 mb-3">About this book</h2>
                <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{ebook.description || ebook.short_description || 'No description available.'}</p>
                {seriesName && ebook.seriesOverview && (
                  <p className="text-xs text-slate-400 leading-relaxed mt-3 border-t border-slate-100 pt-3">
                    <span className="font-bold text-slate-500">{seriesName}: </span>{ebook.seriesOverview}
                  </p>
                )}
              </div>

              {/* What this book covers — tagged assessment chapters */}
              <div className="bg-white border border-slate-200 rounded-2xl p-6">
                <h2 className="text-base font-black text-slate-900 mb-1">What this book covers</h2>
                <p className="text-xs text-slate-400 mb-4">Assessment chapters this book is mapped to.</p>
                {coveredChapters.length === 0 ? (
                  <p className="text-sm text-slate-400 py-4 text-center">Not tagged to any assessment chapters yet.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {coveredChapters.map((c) => (
                      <span key={c.id} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-100">
                        <Tag size={11}/> {c.name}{c.source ? <span className="text-indigo-400 font-normal"> · {c.source}</span> : null}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Prerequisites & related content */}
              <div className="bg-white border border-slate-200 rounded-2xl p-6">
                <h2 className="text-base font-black text-slate-900 mb-1">Prerequisites &amp; related content</h2>
                <p className="text-xs text-slate-400 mb-4">Recommended before or alongside this book.</p>
                {!hasPrereqs ? (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center mb-3">
                      <Check size={18} className="text-slate-400"/>
                    </div>
                    <p className="text-sm font-semibold text-slate-600">No prerequisites</p>
                    <p className="text-xs text-slate-400 mt-1">Anyone can start reading this book</p>
                  </div>
                ) : (
                  <div className="flex flex-col gap-5">
                    {prereqCourses.length > 0 && (
                      <div>
                        <p className="text-xs font-black text-blue-600 uppercase tracking-wider mb-2.5">Courses</p>
                        {prereqCourses.map(c => (
                          <div key={c.id} className="flex items-center gap-2.5 text-sm text-slate-700 py-2 border-b border-slate-50 last:border-0">
                            <Layers size={13} className="text-blue-400 flex-shrink-0"/>{c.name || `Course #${c.id}`}
                          </div>
                        ))}
                      </div>
                    )}
                    {prereqAssessments.length > 0 && (
                      <div>
                        <p className="text-xs font-black text-amber-600 uppercase tracking-wider mb-2.5">Assessments</p>
                        {prereqAssessments.map(a => (
                          <div key={a.id} className="flex items-center gap-2.5 text-sm text-slate-700 py-2 border-b border-slate-50 last:border-0">
                            <Award size={13} className="text-amber-400 flex-shrink-0"/>{a.name || `Assessment #${a.id}`}
                          </div>
                        ))}
                      </div>
                    )}
                    {prereqEbooks.length > 0 && (
                      <div>
                        <p className="text-xs font-black text-indigo-600 uppercase tracking-wider mb-2.5">E-Books</p>
                        {prereqEbooks.map(e => (
                          <div key={e.id} className="flex items-center gap-2.5 text-sm text-slate-700 py-2 border-b border-slate-50 last:border-0">
                            <BookOpen size={13} className="text-indigo-400 flex-shrink-0"/>{e.name || `E-Book #${e.id}`}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ══ CURRICULUM ═══════════════════════════════════════ */}
          {tab === 1 && (
            <div className="flex flex-col gap-4">
              <p className="text-xs font-black text-slate-500 uppercase tracking-wider">
                {chapters.length} chapters &nbsp;•&nbsp; {totalTopics} topics
              </p>
              {chapters.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-2xl flex flex-col items-center justify-center py-16">
                  <BookOpen size={36} className="text-slate-200 mb-3"/>
                  <p className="text-sm font-semibold text-slate-500">No chapters yet</p>
                </div>
              ) : chapters.map((ch, ci) => {
                const topics = ch.topics || [];
                const done   = topics.filter(t => t.progressStatus === 'completed').length;
                const pct    = topics.length > 0 ? Math.round((done / topics.length) * 100) : 0;
                const isOpen = openCh[ch.chapterId] ?? ci === 0;
                return (
                  <div key={ch.chapterId} className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                    {/* ── chapter header ─────────────────────────────── */}
                    <button type="button" onClick={() => toggleCh(ch.chapterId, ci)}
                      className="w-full flex items-center gap-3 px-4 sm:px-5 py-4 text-left hover:bg-slate-50 transition-colors">
                      <div className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center text-sm font-black flex-shrink-0 border border-indigo-100">
                        {ci + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-slate-900 truncate">{ch.title}</p>
                        <p className="text-xs text-slate-400 mt-0.5">{topics.length} topic{topics.length !== 1 ? 's' : ''}</p>
                      </div>
                      {isEnrolled && topics.length > 0 && (
                        <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full flex-shrink-0 ${pct === 100 ? 'bg-emerald-50 text-emerald-700' : 'bg-indigo-50 text-indigo-600'}`}>
                          {done}/{topics.length}
                        </span>
                      )}
                      {isOpen ? <ChevronUp size={18} className="text-slate-400 flex-shrink-0"/> : <ChevronDown size={18} className="text-slate-400 flex-shrink-0"/>}
                    </button>

                    {/* ── chapter body ──────────────────────────────── */}
                    {isOpen && (
                      <div className="border-t border-slate-100 px-4 sm:px-5 py-4 flex flex-col gap-3">
                        {ch.description && (
                          <p className="text-sm text-slate-500 italic leading-relaxed">{ch.description}</p>
                        )}
                        {topics.length === 0 ? (
                          <p className="text-xs text-slate-400">No topics yet.</p>
                        ) : topics.map((t) => {
                          const status = t.progressStatus;
                          return (
                            <div key={t.topicId}
                              className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 hover:border-indigo-200 transition-colors">
                              {status === 'completed'
                                ? <CheckCircle size={18} className="text-emerald-500 flex-shrink-0"/>
                                : <Circle size={18} className={`flex-shrink-0 ${status === 'in_progress' ? 'text-indigo-500' : 'text-slate-300'}`}/>}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-slate-900 truncate">{t.title}</p>
                                <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 mt-1 text-xs text-slate-400">
                                  {t.readTimeMinutes > 0 && (
                                    <span className="flex items-center gap-1"><Clock size={11}/> {t.readTimeMinutes} mins</span>
                                  )}
                                  {isEnrolled && status === 'completed'   && <span className="text-emerald-600 font-semibold">• Completed</span>}
                                  {isEnrolled && status === 'in_progress' && <span className="text-indigo-600 font-semibold">• In progress</span>}
                                  {t.hasVideo && <span className="text-rose-500 font-semibold">• Video</span>}
                                  {t.hasAssessment && <span className="text-amber-600 font-semibold">• Quiz</span>}
                                </div>
                              </div>
                              {canRead && (
                                <button type="button" onClick={() => handleReadTopic(t.topicId)}
                                  className="text-xs font-bold text-indigo-600 hover:text-indigo-800 whitespace-nowrap flex-shrink-0 transition-colors">
                                  Read Topic
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* ══ ANALYTICS ════════════════════════════════════════ */}
          {tab === 2 && (
            analyticsLoading ? (
              <AnalyticsSkeleton/>
            ) : analyticsError ? (
              <div className="bg-white border border-slate-200 rounded-2xl flex flex-col items-center justify-center py-20 text-center px-6">
                <p className="text-sm font-black text-slate-700 mb-1">Couldn't load analytics</p>
                <p className="text-xs text-slate-400 max-w-xs mb-4">Something went wrong fetching your analytics. Please try again.</p>
                <button type="button" onClick={() => handleTabClick(2)}
                  className="px-5 py-2.5 rounded-xl bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 transition-colors">
                  Retry
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-6">

                {!hasStarted && (
                  <div className="bg-indigo-50 border border-indigo-100 rounded-2xl px-5 py-4 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-white flex items-center justify-center flex-shrink-0">
                      <TrendingUp size={16} className="text-indigo-500"/>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-bold text-slate-800">Your personal analytics will fill in as you read</p>
                      <p className="text-xs text-slate-500">The book structure and estimates are shown below — start reading to track streaks, scores and progress.</p>
                    </div>
                    <button type="button" onClick={handleContinue}
                      className="px-4 py-2 rounded-xl bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 transition-colors flex items-center gap-2 shrink-0">
                      <Play size={12}/> Start Reading
                    </button>
                  </div>
                )}

                {/* ── overview cards ─────────────────────────────────── */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <StatCard label="Topics done"   value={`${topicsDone}/${topicsTotal}`} sub={`${completionPct}% complete`}   icon={CheckCircle} iconColor="#10b981" iconBg="#d1fae5"/>
                  <StatCard label="Time spent"    value={totalMin >= 60 ? `${Math.floor(totalMin/60)}h ${totalMin%60}m` : `${totalMin}m`} sub={`~${avgTimePerTopic} min/topic`} icon={Clock} iconColor="#6366f1" iconBg="#eef2ff"/>
                  <StatCard label="Current streak" value={`${streakDays}d`} sub={`Best: ${bestStreak}d`} icon={Flame} iconColor="#d97706" iconBg="#fef3c7"/>
                  <StatCard label="Avg score"     value={avgScore > 0 ? `${avgScore}%` : '—'} sub={avgScore > 0 ? (avgScore >= 70 ? 'Good performance' : 'Needs improvement') : 'No assessments yet'} icon={BarChart2} iconColor="#8b5cf6" iconBg="#ede9fe"/>
                </div>

                {/* ── predicted score + score trend ───────────────── */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

                  {/* predicted score */}
                  <div className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col gap-4">
                    <div className="flex items-center gap-2">
                      <Activity size={15} className="text-indigo-500"/>
                      <span className="text-sm font-black text-slate-900">Exam prediction</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div>
                        <p style={{ fontSize: 40, fontWeight: 800, color: '#6366f1', lineHeight: 1, margin: 0 }}>
                          {predicted}<span style={{ fontSize: 16, fontWeight: 400, color: '#94a3b8' }}>/100</span>
                        </p>
                        <p className="text-xs text-slate-400 mt-1">Predicted score</p>
                      </div>
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <PredictBar label="Assessment" value={avgScore}       color="#6366f1"/>
                        <PredictBar label="Completion" value={completionPct}  color="#8b5cf6"/>
                        <PredictBar label="Consistency" value={consistency}   color="#a78bfa"/>
                      </div>
                    </div>
                    {predicted < 70 && (
                      <p className="text-xs text-slate-500 border-t border-slate-100 pt-3">
                        Complete more topics and score 70%+ on assessments to improve your prediction
                      </p>
                    )}
                  </div>

                  {/* score trend */}
                  <div className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col gap-4">
                    <div className="flex items-center gap-2">
                      <TrendingUp size={15} className="text-indigo-500"/>
                      <span className="text-sm font-black text-slate-900">Score trend</span>
                    </div>
                    <ScoreTrend scores={scoreHistory}/>
                    {scoreHistory.length > 0 && (
                      <div className="flex gap-3 border-t border-slate-100 pt-3">
                        <div className="flex-1 text-center">
                          <p className="text-base font-black text-slate-900">{Math.round(Math.max(...scoreHistory))}%</p>
                          <p className="text-xs text-slate-400">Best</p>
                        </div>
                        <div style={{ width: 1, background: '#f1f5f9' }}/>
                        <div className="flex-1 text-center">
                          <p className="text-base font-black text-slate-900">{Math.round(scoreHistory.reduce((a,b)=>a+b,0)/scoreHistory.length)}%</p>
                          <p className="text-xs text-slate-400">Average</p>
                        </div>
                        <div style={{ width: 1, background: '#f1f5f9' }}/>
                        <div className="flex-1 text-center">
                          <p className="text-base font-black text-slate-900">{scoreHistory.length}</p>
                          <p className="text-xs text-slate-400">Attempts</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* ── chapter progress ───────────────────────────────── */}
                <div className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col gap-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Layers size={15} className="text-indigo-500"/>
                      <span className="text-sm font-black text-slate-900">Chapter progress</span>
                    </div>
                    <span className="text-xs text-slate-400">{chapsDone}/{chapsTotal} chapters done</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {anChapters.map((ch, ci) => (
                      <ChapterProgressRow key={ch.chapterId} ch={ch} index={ci}/>
                    ))}
                  </div>
                </div>

                {/* ── nudges ─────────────────────────────────────────── */}
                {nudges.length > 0 && (
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-2">
                      <Target size={15} className="text-indigo-500"/>
                      <span className="text-sm font-black text-slate-900">What to do next</span>
                    </div>
                    {nudges.map((n, i) => <Nudge key={i} {...n}/>)}
                  </div>
                )}

                {/* ── achievements ───────────────────────────────────── */}
                <div className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col gap-4">
                  <div className="flex items-center gap-2">
                    <Star size={15} className="text-indigo-500"/>
                    <span className="text-sm font-black text-slate-900">Achievements</span>
                    <span className="text-xs font-bold bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full ml-auto">
                      {badges.filter(b => b.earned).length}/{badges.length} earned
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {badges.map((b, i) => <Badge key={i} {...b}/>)}
                  </div>
                </div>

                {/* ── expand toggle ───────────────────────────────────── */}
                <button type="button" onClick={() => setShowDetail(p => !p)}
                  className="flex items-center justify-center gap-2 py-3 text-sm text-slate-500 hover:text-slate-700 font-semibold border border-slate-200 bg-white rounded-2xl transition-colors">
                  {showDetail ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
                  {showDetail ? 'Hide detailed stats' : 'View detailed stats'}
                </button>

                {/* ── detail section ──────────────────────────────────── */}
                {showDetail && (
                  <div className="flex flex-col gap-4">

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                      {/* reading behaviour */}
                      <div className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col gap-3">
                        <div className="flex items-center gap-2 mb-1">
                          <Activity size={15} className="text-indigo-500"/>
                          <span className="text-sm font-black text-slate-900">Reading behaviour</span>
                        </div>
                        {[
                          { icon: Clock,      label: 'Avg per topic',  value: avgTimePerTopic > 0 ? `${avgTimePerTopic} min` : '—' },
                          { icon: BookOpen,   label: 'Topics today',   value: topicsReadToday },
                          { icon: BarChart2,  label: 'Best study day', value: bestStudyDay },
                        ].map(({ icon: Ic, label, value }) => (
                          <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#64748b' }}>
                              <Ic size={14} style={{ color: '#94a3b8' }}/>{label}
                            </span>
                            <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{value}</span>
                          </div>
                        ))}
                      </div>

                      {/* engagement */}
                      <div className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col gap-3">
                        <div className="flex items-center gap-2 mb-1">
                          <Star size={15} className="text-indigo-500"/>
                          <span className="text-sm font-black text-slate-900">Engagement</span>
                        </div>
                        {[
                          { icon: Highlighter,    label: 'Highlights',     value: highlights },
                          { icon: StickyNote,     label: 'Notes written',  value: notes },
                          { icon: BookMarked,     label: 'Bookmarks',      value: bookmarks },
                        ].map(({ icon: Ic, label, value }) => (
                          <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#64748b' }}>
                              <Ic size={14} style={{ color: '#94a3b8' }}/>{label}
                            </span>
                            <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* time estimate */}
                    {topicsRemaining > 0 && estimatedMinutesLeft != null && (
                      <div className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center gap-4">
                        <div style={{ width: 40, height: 40, borderRadius: 10, background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Clock size={18} style={{ color: '#6366f1' }}/>
                        </div>
                        <div>
                          <p className="text-sm font-black text-slate-900">
                            Estimated {estimatedMinutesLeft} min to finish
                          </p>
                          <p className="text-xs text-slate-400 mt-0.5">
                            {topicsRemaining} topic{topicsRemaining !== 1 ? 's' : ''} remaining based on your average reading pace
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

              </div>
            )
          )}
        </div>
      </div>
    </Page>
  );
}